/**
 * MODULE: _shared/frontend/lib/frontend/utils/assistantRouteFocusHint.ts
 * PURPOSE: Derive focus_entity_hint from shell route for brain context on the assistant dock [2.2.1255].
 * RECENT: 2026-06-08 [2.2.1345] — Pages, calendar events, CRM account paths; merge with selectedRecord.
 * PUBLIC_API: focusEntityHintFromRoute, mergeFocusEntityHintFromShellRoute, inferShellModuleFromPath
 * AGENT_SAFETY: SAFE
 */

export type RouteFocusEntityHint = {
  kind: string;
  entity_id: string;
};

export type ShellRouteFocusInput = {
  route: string;
  selectedRecord?: string | null;
};

function parseShellUrl(route: string): URL | null {
  const raw = (route || '').trim();
  if (!raw) return null;
  try {
    return new URL(raw.startsWith('/') ? raw : `/${raw}`, 'http://brazn.local');
  } catch {
    return null;
  }
}

/** Map shell routes to context/resolve focus_entity_hint (CRM, Pages, calendar). */
export function focusEntityHintFromRoute(route: string): RouteFocusEntityHint | null {
  const url = parseShellUrl(route);
  if (!url) return null;

  const path = url.pathname.replace(/\/+$/, '');

  const dealMatch = path.match(/^\/crm\/deals\/([^/]+)$/i);
  if (dealMatch?.[1] && dealMatch[1] !== 'new') {
    return { kind: 'deal', entity_id: decodeURIComponent(dealMatch[1]) };
  }

  const contactPathMatch = path.match(/^\/crm\/contacts\/([^/]+)$/i);
  if (contactPathMatch?.[1] && contactPathMatch[1] !== 'new') {
    return { kind: 'contact', entity_id: decodeURIComponent(contactPathMatch[1]) };
  }

  const accountPathMatch = path.match(/^\/crm\/accounts\/([^/]+)$/i);
  if (accountPathMatch?.[1] && accountPathMatch[1] !== 'new') {
    return { kind: 'account', entity_id: decodeURIComponent(accountPathMatch[1]) };
  }

  const contactId = url.searchParams.get('contactId')?.trim();
  if (contactId) return { kind: 'contact', entity_id: contactId };

  const accountId = url.searchParams.get('accountId')?.trim();
  if (accountId) return { kind: 'account', entity_id: accountId };

  const pageMatch = path.match(/^\/workspaces\/[^/]+\/pages\/([^/]+)$/i);
  if (pageMatch?.[1] && pageMatch[1] !== 'new') {
    return { kind: 'page', entity_id: decodeURIComponent(pageMatch[1]) };
  }

  const legacyPageMatch = path.match(/^\/pages\/legacy\/([^/]+)$/i);
  if (legacyPageMatch?.[1]) {
    return { kind: 'page', entity_id: decodeURIComponent(legacyPageMatch[1]) };
  }

  const calendarEventMatch = path.match(/^\/calendar\/events\/([^/]+)$/i);
  if (calendarEventMatch?.[1] && calendarEventMatch[1] !== 'new') {
    return { kind: 'event', entity_id: decodeURIComponent(calendarEventMatch[1]) };
  }

  return null;
}

/** Prefer explicit path entity ids; fall back to CRM drawer/list selection from routeMeta. */
export function mergeFocusEntityHintFromShellRoute(
  input: ShellRouteFocusInput
): RouteFocusEntityHint | null {
  const fromRoute = focusEntityHintFromRoute(input.route);
  if (fromRoute) return fromRoute;

  const record = (input.selectedRecord ?? '').trim();
  if (!record) return null;

  const url = parseShellUrl(input.route);
  if (!url) return null;
  const path = url.pathname.replace(/\/+$/, '').toLowerCase();

  if (path.includes('/crm/deals')) return { kind: 'deal', entity_id: record };
  if (path.includes('/crm/contacts')) return { kind: 'contact', entity_id: record };
  if (path.includes('/crm/accounts')) return { kind: 'account', entity_id: record };
  if (path.includes('/workspaces/') && path.includes('/pages')) {
    return { kind: 'page', entity_id: record };
  }
  if (path.includes('/calendar')) return { kind: 'event', entity_id: record };

  return null;
}

/** Coarse module key for shell_context on context/resolve [2.2.1345]. */
export function inferShellModuleFromPath(route: string): string {
  const url = parseShellUrl(route);
  const path = (url?.pathname ?? route).replace(/\/+$/, '').toLowerCase() || '/';

  if (path === '/crm' || path.startsWith('/crm/')) return 'crm';
  if (path.includes('/calendar')) return 'calendar';
  if (path === '/pages' || path.startsWith('/pages/') || path.includes('/workspaces/')) return 'pages';
  if (path.includes('/sales-hub')) return 'sales-hub';
  if (path.includes('/messaging')) return 'messaging';
  if (path.includes('/activities') || path.includes('/activity')) return 'activities';
  if (path === '/home' || path === '/') return 'home';
  if (path.includes('/langflow-assistant')) return 'assistant';
  return 'workspace';
}
